import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import type { StackNavigationProp } from "@react-navigation/stack";
import { LinearGradient } from 'expo-linear-gradient';
import React, { useMemo, useCallback, useEffect, useState } from "react";
import {
  View,
  Text,
  ActivityIndicator,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  Alert,
  StatusBar,
} from "react-native";
import { Share } from "react-native";
import tw from "twrnc";

import { t } from "../../../i18n/translations";
import type { RootStackParamList } from "../../../navigation/types";
import { generateRoomLink } from "../../../shared/services/deepLink";
import { iqesColors, iqesGradients } from "../../../shared/ui/iqesDesignSystem";
import type { Player } from "../../../types/multiplayer";
import { useLanguageStore } from "../../quiz-core/languageStore";
import { useUserProfile } from "../../quiz-core/useStore"; // Ensure this path is correct
import MultiplayerPlayerStatusList from "../components/MultiplayerPlayerStatusList";
import PlayersList from "../components/PlayersList";
import QuizProgressBar from "../components/QuizProgressBar";
import RoomActions from "../components/RoomActions";
import RoomHeader from "../components/RoomHeader";
import { useGameRoom } from "../hooks/useGameRoom";
import { resolveRoomGroupMeta } from "../utils/groupMeta";
import { uiText } from '../../../shared/utils/uiText';
// LobbyChat import removed for MVP - focusing on core game experience

// Legacy quiz content imports removed - quiz now handled by UnifiedQuizRenderer in MultiplayerQuizScreen

// Type for route params
type GameRoomRouteProp = RouteProp<RootStackParamList, "GameRoom">;

// Legacy quiz question types and mapping removed - GameRoomScreen is now lobby-only

// DEBUG: Log every render of GameRoomScreen

export default function GameRoomScreen() {
  const route = useRoute<GameRoomRouteProp>();
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const roomId = route.params?.roomId;
  const routeFamilyId = route.params?.familyId;
  const routeFamilyName = route.params?.familyName;
  const routeFriendsGroupId = route.params?.friendsGroupId;
  const routeFriendsGroupName = route.params?.friendsGroupName;
  const { language } = useLanguageStore();

  const {
    currentRoom,
    players,
    isHost,
    isReady,
    isLoading,
    presenceReady: _presenceReady,
    messages: _messages,
    handleToggleReady,
    handleStartGame,
    handleLeaveRoom,
    handleSendMessage: _handleSendMessage,
    isTyping: _isTyping,
    currentUser,
    error,
  } = useGameRoom(roomId || "", {
    familyId: routeFamilyId,
    familyName: routeFamilyName,
    friendsGroupId: routeFriendsGroupId,
    friendsGroupName: routeFriendsGroupName,
  });

  // Debug log for loading and presence removed (DS cleanup)

  const _userProfile = useUserProfile();
  const [isStartingGameSession, setIsStartingGameSession] = useState(false);
  const [_showWaitingForSessionId, setShowWaitingForSessionId] = useState(false);
  const startedSessionRef = React.useRef<string | null>(null);

  // DEBUG logs removed (DS cleanup)

  // Navigate to MultiplayerQuiz when the game starts
  useEffect(() => {
    if (currentRoom?.status === 'completed') {
      startedSessionRef.current = null;
      return;
    }

    if (currentRoom?.status === "in_progress") {
      if (currentRoom.current_session_id) {
        if (startedSessionRef.current === currentRoom.current_session_id) {
          return;
        }
        startedSessionRef.current = currentRoom.current_session_id;
        navigation.navigate("MultiplayerQuiz", {
          roomId: currentRoom.id,
          sessionId: currentRoom.current_session_id,
          multiplayer: true,
          isHost,
          familyId: routeFamilyId,
          familyName: routeFamilyName,
          friendsGroupId: routeFriendsGroupId,
          friendsGroupName: routeFriendsGroupName,
        });
      } else {
        setShowWaitingForSessionId(true);
      }
      return;
    }
  }, [currentRoom?.status, currentRoom?.current_session_id, currentRoom?.id, navigation, isHost, routeFamilyId, routeFamilyName, routeFriendsGroupId, routeFriendsGroupName]);

  const onHostStartGamePress = useCallback(async () => {
    if (!currentRoom?.id) {
      Alert.alert(uiText('errorTitle'), uiText('noValidRoomId'));
      return;
    }
    setIsStartingGameSession(true);
    try {
      await handleStartGame();
    } catch (error) {
      Alert.alert(
        uiText('startGameFailedTitle'),
        error instanceof Error ? error.message : String(error),
      );
      // Start game error handled by Alert above
    } finally {
      setIsStartingGameSession(false);
    }
  }, [currentRoom?.id, isHost, handleStartGame]);

  // --- Removed all quiz logic, state, and handlers ---
  // Only lobby logic and navigation to MultiplayerQuizScreen remains.

  const currentUserId = currentUser?.id;
  const hostId: string | null = currentRoom?.host_id || null;

  const handleLeave = useCallback(async () => {
    if (!roomId) return;
    try {
      await handleLeaveRoom();
      navigation.goBack();
    } catch (_error) {
      // Error leaving room handled silently
    }
  }, [roomId, handleLeaveRoom, navigation]);

  const currentPlayer = useMemo(() => {
    if (!currentUserId) return null;
    return (
      players.find((player: Player) => player.id === currentUserId) || null
    );
  }, [currentUserId, players]);

  const effectiveHostId = useMemo(() => {
    if (hostId) return hostId;
    const explicitHost = players.find((player: Player) => player.is_host);
    if (explicitHost?.id) return explicitHost.id;
    if (isHost && currentUserId) return currentUserId;
    return null;
  }, [currentUserId, hostId, isHost, players]);

  const hostPlayer = useMemo(() => {
    if (!effectiveHostId) {
      return isHost ? currentPlayer : null;
    }
    return players.find((player: Player) => player.id === effectiveHostId) || (isHost ? currentPlayer : null);
  }, [currentPlayer, effectiveHostId, isHost, players]);

  const allPlayersReady = useMemo(() => {
    if (players.length === 0) return false;
    return players.every((player: Player) => player.is_ready);
  }, [players]);

  const canStartGame = useMemo(() => {
    return allPlayersReady && players.length >= 2;
  }, [allPlayersReady, players.length]);

  const shouldShowReadyCard = useMemo(() => {
    return players.length >= 2 || Boolean(currentPlayer?.is_ready);
  }, [currentPlayer?.is_ready, players.length]);

  const readyStateSubtitle = useMemo(() => {
    if (players.length < 2) {
      return t('multiplayer_lobby.waitingForPlayers', language);
    }

    if (allPlayersReady) {
      return isHost
        ? t('multiplayer_lobby.startQuiz', language)
        : t('multiplayer_lobby.waitingForHostToStart', language);
    }

    return isHost
      ? t('multiplayer_lobby.playersInRoom', language).replace('{{count}}', players.length.toString())
      : t('multiplayer_lobby.readyUpToStart', language);
  }, [allPlayersReady, isHost, language, players.length]);

  const roomLink = useMemo(
    () => (roomId && currentRoom?.code ? generateRoomLink(roomId, currentRoom.code) : ""),
    [currentRoom?.code, roomId],
  );
  const roomMeta = useMemo(
    () =>
      resolveRoomGroupMeta(
        {
          ...(currentRoom ?? {}),
          family_id: routeFamilyId ?? currentRoom?.family_id,
          family_name: routeFamilyName ?? currentRoom?.family_name,
          friends_group_id: routeFriendsGroupId ?? currentRoom?.friends_group_id,
          friends_group_name: routeFriendsGroupName ?? currentRoom?.friends_group_name,
          room_type:
            routeFamilyId || routeFamilyName
              ? 'family'
              : routeFriendsGroupId || routeFriendsGroupName
                ? 'friends'
                : currentRoom?.room_type,
        },
        language,
      ),
    [
      currentRoom,
      language,
      routeFamilyId,
      routeFamilyName,
      routeFriendsGroupId,
      routeFriendsGroupName,
    ],
  );

  const statusSummary = useMemo(() => {
    if (players.length <= 1) {
      return t('multiplayer_lobby.waitingForPlayers', language);
    }

    if (allPlayersReady) {
      return t('multiplayer_lobby.allPlayersReady', language);
    }

    return t('multiplayer_lobby.playersInRoom', language).replace('{{count}}', players.length.toString());
  }, [allPlayersReady, isHost, language, players.length]);

  const handleShareRoom = useCallback(() => {
    if (!roomLink) return;
    const shareMessage = t('familiesFriends.inviteShareRoomMessage', language)
      .replace('{{code}}', currentRoom?.code || '')
      .replace('{{url}}', roomLink);

    Share.share({
      message: shareMessage,
      title: t('familiesFriends.inviteShareTitle', language).replace('{{title}}', currentRoom?.name || t('multiplayer_lobby.quizRoom', language)),
      url: roomLink,
    });
  }, [currentRoom?.code, currentRoom?.name, language, roomLink]);

  // --- Render Logic ---
  // Block all UI until multiplayer state is fully ready
  if (isLoading) {
    return (
      <SafeAreaView style={styles.centered} data-oid="juj.vbp">
        <ActivityIndicator size="large" color={iqesColors.goldBright} data-oid="30:tq3m" />
        <Text style={styles.loadingText} data-oid="xggdoo8">{t('multiplayer_lobby.loadingMultiplayerRoom', language)}</Text>
      </SafeAreaView>
    );
  }

  if (error) {
    return (
      <SafeAreaView style={styles.centered} data-oid="an1s3bk">
        <Text style={styles.errorText} data-oid="zakhy4j">
          {t('quickActions.error', language)}: {error}
        </Text>
      </SafeAreaView>
    );
  }

  if (!currentRoom || typeof currentRoom.status === "undefined") {
    return (
      <SafeAreaView style={styles.centered} data-oid="n6::w80">
        <ActivityIndicator size="large" color={iqesColors.goldBright} data-oid="nzmie.k" />
        <Text style={styles.loadingText} data-oid="gdu6q2x">{t('multiplayer_lobby.loadingRoomDetails', language)}</Text>
      </SafeAreaView>
    );
  }

  // Only lobby UI remains
  return (
    <LinearGradient colors={iqesGradients.appBackground} style={styles.screen}>
      <StatusBar barStyle="light-content" backgroundColor={iqesColors.canvas} />
      <View style={styles.orbTop} pointerEvents="none" />
      <View style={styles.orbBottom} pointerEvents="none" />
        <SafeAreaView style={styles.container} data-oid="o-vxwrq">
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          style={tw`flex-1`}
          data-oid="gwyho.3"
        >
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ flexGrow: 1, paddingBottom: 20 }}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={false}
            bounces={true}
          >
            <View style={styles.contentWidth}>
            {/* Back navigation button */}
            <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingTop: 8 }}>
              <TouchableOpacity
                onPress={() => navigation.goBack()}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  backgroundColor: 'rgba(8, 10, 24, 0.92)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderWidth: 1,
                  borderColor: 'rgba(246, 190, 58, 0.38)',
                }}
                accessibilityLabel={t('multiplayer_lobby.goBackAccessibility', language)}
                accessibilityRole="button"
              >
                <Ionicons name="arrow-back" size={20} color={iqesColors.ivory} />
              </TouchableOpacity>
            </View>

            <View style={styles.headerShell}>
              <RoomHeader
                onShare={isHost ? handleShareRoom : undefined}
                roomName={currentRoom?.category_name
                  ? `${currentRoom.category_name} ${t('multiplayer_lobby.quizRoom', language)}`
                  : roomMeta.roomTitle}
                hostName={hostPlayer?.name || ""}
                onLeave={handleLeave}
                data-oid="6vfaahp"
                accessibilityLabel={`Quiz room header. Host: ${hostPlayer?.name || ''}`}
                accessibilityRole="header"
                accessible
              />
            </View>

            <View style={{ paddingHorizontal: 16 }}>
              <View style={styles.roomPanel}>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <View style={{ flex: 1, paddingRight: 12 }}>
                    <Text
                      style={styles.panelEyebrow}
                    >
                      {t('multiplayer_lobby.roomDetails', language)}
                    </Text>
                    <Text style={styles.panelTitle}>
                      {statusSummary}
                    </Text>
                    <Text style={styles.panelSubtitle}>
                      {currentRoom?.code
                        ? `${roomMeta.groupLabel} · ${t('multiplayer_lobby.gameRoomCode', language)} ${currentRoom.code}`
                        : t('multiplayer_lobby.waitingForPlayers', language)}
                    </Text>
                  </View>
                  <View
                    style={{
                      minWidth: 72,
                      paddingHorizontal: 12,
                      paddingVertical: 10,
                      borderRadius: 16,
                      backgroundColor: 'rgba(246, 190, 58, 0.15)',
                      borderWidth: 1,
                      borderColor: 'rgba(246, 190, 58, 0.38)',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{ color: iqesColors.goldSoft, fontSize: 12, fontWeight: '900', textTransform: 'uppercase' }}>
                      {players.length}
                    </Text>
                    <Text style={{ color: iqesColors.mutedWarm, fontSize: 11, fontWeight: '700' }}>
                      {t('multiplayer_lobby.players', language)}
                    </Text>
                  </View>
                </View>
                {shouldShowReadyCard ? (
                  <View
                    style={{
                      backgroundColor: 'rgba(255, 248, 232, 0.07)',
                      borderRadius: 20,
                      padding: 16,
                      marginBottom: 16,
                      borderWidth: 1,
                      borderColor: 'rgba(246, 190, 58, 0.22)',
                    }}
                  >
                  <View style={{ marginBottom: 12 }}>
                    <Text
                      style={styles.panelEyebrow}
                    >
                      {t('multiplayer_lobby.readyToBegin', language)}
                    </Text>
                    <Text style={styles.panelSubtitle}>
                      {readyStateSubtitle}
                    </Text>
                  </View>
                  <RoomActions
                    isHost={isHost}
                    playerIsReady={currentPlayer?.is_ready || false}
                    canStartGame={canStartGame}
                    onToggleReady={handleToggleReady}
                    onStartGame={onHostStartGamePress}
                    isStartingGame={isStartingGameSession}
                    playerCount={players.length}
                    data-oid="26o6e14"
                    accessible
                  />
                  </View>
                ) : null}
                {/* Progress Bar: only show if quiz state exists */}
                {typeof currentRoom?.current_question === 'number' && typeof currentRoom?.total_questions === 'number' ? (
                  <QuizProgressBar
                    current={currentRoom.current_question ?? 0}
                    total={currentRoom.total_questions ?? 0}
                  />
                ) : null}
                <PlayersList
                  players={players}
                  hostId={hostId}
                  currentUserId={currentUserId}
                  isCurrentPlayerReady={isReady}
                  onToggleReady={handleToggleReady}
                  loadingStart={isStartingGameSession}
                  data-oid="s4zi.:n"
                  accessibilityLabel={t('multiplayer_lobby.playersListAccessibility', language)}
                  accessibilityRole="list"
                  accessible
                />
                <MultiplayerPlayerStatusList
                  players={players}
                />
              </View>
            </View>

            <View style={tw`px-4 pb-4`} data-oid="q_qboc2">
            </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    backgroundColor: iqesColors.canvas,
  },
  loadingText: {
    color: iqesColors.mutedWarm,
    marginTop: 12,
  },
  container: {
    flex: 1,
  },
  contentWidth: {
    width: '100%',
    maxWidth: 520,
    alignSelf: 'center',
  },
  headerShell: {
    margin: 16,
    marginBottom: 12,
    borderRadius: 28,
  },
  roomPanel: {
    marginBottom: 16,
    borderRadius: 24,
    padding: 18,
    backgroundColor: 'rgba(8, 10, 24, 0.9)',
    borderWidth: 1,
    borderColor: 'rgba(246, 190, 58, 0.38)',
    shadowColor: iqesColors.gold,
    shadowOpacity: 0.16,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 8 },
    elevation: 8,
  },
  panelEyebrow: {
    color: iqesColors.goldSoft,
    fontSize: 13,
    fontWeight: '900',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginBottom: 4,
  },
  panelTitle: {
    color: iqesColors.ivory,
    fontSize: 20,
    fontWeight: '900',
    marginBottom: 4,
  },
  panelSubtitle: {
    color: iqesColors.mutedWarm,
    fontSize: 14,
    lineHeight: 20,
  },
  quizContainer: {
    flex: 1,
  },
  errorText: {
    color: '#FCA5A5',
    fontSize: 16,
  },
  orbTop: {
    position: 'absolute',
    top: -90,
    right: -90,
    width: 260,
    height: 260,
    borderRadius: 130,
    backgroundColor: 'rgba(139, 92, 246, 0.18)',
  },
  orbBottom: {
    position: 'absolute',
    bottom: -120,
    left: -120,
    width: 300,
    height: 300,
    borderRadius: 150,
    backgroundColor: 'rgba(246, 190, 58, 0.09)',
  },
  submittingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(29,26,35,0.3)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
  },
  submittingText: {
    marginTop: 10,
    fontSize: 16,
  },
});
